import { ErrorCode, type ApiErrorBody } from '@kainos/shared';

/** Error thrown by the HTTP client. Carries the API's stable error code. */
export class ApiError extends Error {
  readonly code: ApiErrorBody['code'];
  readonly status: number;
  readonly details: NonNullable<ApiErrorBody['details']>;
  readonly requestId?: string;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details ?? [];
    this.requestId = body.requestId;
  }

  /** Retrying will not help; the caller should surface the message. */
  get isClientFault(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  get isAuthFailure(): boolean {
    return this.code === ErrorCode.UNAUTHORIZED || this.code === ErrorCode.FORBIDDEN;
  }
}

/** Spanish copy for the error codes a user can actually run into. */
const MESSAGES: Partial<Record<ApiErrorBody['code'], string>> = {
  [ErrorCode.UNAUTHORIZED]: 'Tu sesión expiró. Vuelve a iniciar sesión.',
  [ErrorCode.FORBIDDEN]: 'No tienes permisos para realizar esta acción.',
  [ErrorCode.TENANT_MISMATCH]: 'No tienes acceso a esta organización.',
  [ErrorCode.NOT_FOUND]: 'No encontramos lo que buscabas.',
  [ErrorCode.VALIDATION_FAILED]: 'Revisa los datos ingresados.',
  [ErrorCode.PAYLOAD_TOO_LARGE]: 'El archivo supera el tamaño permitido.',
  [ErrorCode.RATE_LIMITED]: 'Demasiadas solicitudes. Intenta nuevamente en unos segundos.',
  [ErrorCode.AI_UNAVAILABLE]: 'KainOS AI no está disponible en este momento.',
  [ErrorCode.AI_INVALID_OUTPUT]:
    'No pudimos interpretar la respuesta. Intenta reformular el reporte.',
  [ErrorCode.INTERNAL]: 'Ocurrió un error inesperado. Ya estamos revisándolo.',
};

export function messageForError(error: unknown): string {
  if (error instanceof ApiError) {
    return MESSAGES[error.code] ?? error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Ocurrió un error inesperado.';
}
